import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { Modal, Button, Form, Table } from "react-bootstrap";
import Layout from "../components/Layout";
import appConstants from "../constants/appConstants";

const getAuthToken = () => localStorage.getItem("authToken");

const FeeManagement = () => {
  const { student_id } = useParams();
  const navigate = useNavigate();
  const [fees, setFees] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [student, setStudent] = useState(null);
  const [currentFee, setCurrentFee] = useState({
    id: null,
    amount: "",
    date: "",
    remark: "",
  });

  useEffect(() => {
    fetchStudentDetails();
  }, []);

  useEffect(() => {
    if (student) fetchFees();
  }, [student]);

  const fetchStudentDetails = async () => {
    const token = getAuthToken();
    if (!token) {
      alert("Unauthorized! Please log in.");
      navigate("/login");
      return;
    }

    try {
      const response = await axios.post(
        `${appConstants.API_ROOT_URL}get-student`,
        { id: student_id },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.status === "success") {
        setStudent(response.data.data);
      } else {
        alert("Failed to fetch student details.");
        navigate(-1);
      }
    } catch (err) {
      alert("Error fetching student details.");
      navigate(-1);
    }
  };

  const fetchFees = async () => {
    const token = getAuthToken();
    if (!token) {
      alert("Unauthorized! Please log in.");
      navigate("/login");
      return;
    }
    try {
      const response = await axios.post(
        `${appConstants.API_ROOT_URL}get-student-fees`,
        { student_id },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.status === "success") {
        setFees(response.data.fees);
      } else {
        alert("Failed to fetch fees.");
        navigate(-1);
      }
    } catch (error) {
      console.error("Error fetching fees:", error);
    }
  };

  const handleShowModal = (fee = {}) => {
    setCurrentFee({
      id: fee.id || null,
      amount: fee.amount || "",
      date: fee.date || "",
      remark: fee.remark || "",
    });
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setCurrentFee({ id: null, amount: "", date: "", remark: "" });
  };

  const handleSaveFee = async () => {
    try {
      const token = getAuthToken();
      if (!token) {
        alert("Unauthorized! Please log in.");
        navigate("/login");
        return;
      }
  
      const payload = { ...currentFee, student_id: student.id };
      const url = currentFee.id
        ? `${appConstants.API_ROOT_URL}update-fee`
        : `${appConstants.API_ROOT_URL}create-fee`;
  
      const response = await axios.post(url, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
  
      if (response.data.status === "success") {
        //alert(response.data.message || "Fee saved successfully!");
        fetchFees();
        handleCloseModal();
      } else {
        alert(response.data.message || "Failed to save fee. Please try again.");
      }
    } catch (error) {
      console.error("Error saving fee:", error);
      alert(
        error.response?.data?.message ||
          "An unexpected error occurred. Please check your connection and try again."
      );
    }
  };
  

  const handleDeleteFee = async (id) => {
    if (!window.confirm("Are you sure you want to delete this fee?")) {
      return;
    }
  
    try {
      const token = getAuthToken();
      if (!token) {
        alert("Unauthorized! Please log in.");
        navigate("/login");
        return;
      }
  
      const response = await axios.post(
        `${appConstants.API_ROOT_URL}delete-fee`,
        { id },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
  
      if (response.data.status === "success") {
        //alert(response.data.message || "Fee deleted successfully!");
        fetchFees();
      } else {
        alert(response.data.message || "Failed to delete fee. Please try again.");
      }
    } catch (error) {
      console.error("Error deleting fee:", error);
      alert(
        error.response?.data?.message ||
          "An unexpected error occurred. Please check your connection and try again."
      );
    }
  };
  

  return (
    <Layout>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h2>
          <i className="fas fa-money-bill-wave"></i> Fee: {" "}
          <span className="text-primary">{student ? student.name : "Loading..."}</span>
        </h2>
        {/* Back Button */}
        <Button variant="secondary" onClick={() => navigate(-1)}>
          <i className="fas fa-arrow-left"></i> Back
        </Button>
      </div>

      <Button variant="primary" className="mb-3" onClick={() => handleShowModal()}>
        <i className="fas fa-plus"></i> Add Fee
      </Button>

      <Table striped bordered hover>
        <thead>
          <tr>
            <th>ID</th>
            <th>Amount</th>
            <th>Date</th>
            <th>Remark</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {fees.length > 0 ? (
            fees.map((fee, index) => (
              <tr key={fee.id}>
                <td>{index + 1}</td>
                <td>{fee.amount}</td>
                <td>{fee.date}</td>
                <td>{fee.remark}</td>
                <td>
                  <Button variant="warning" size="sm" onClick={() => handleShowModal(fee)}>
                    <i className="fas fa-edit"></i>
                  </Button>{" "}
                  <Button variant="danger" size="sm" onClick={() => handleDeleteFee(fee.id)}>
                    <i className="fas fa-trash"></i>
                  </Button>
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan="5" className="text-center">No fees found.</td>
            </tr>
          )}
        </tbody>
      </Table>

      {/* Modal for Adding/Editing Fee */}
      <Modal show={showModal} onHide={handleCloseModal}>
        <Modal.Header closeButton>
          <Modal.Title>{currentFee.id ? "Edit Fee" : "Add Fee"}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group>
              <Form.Label>Amount</Form.Label>
              <Form.Control
                type="number"
                value={currentFee.amount}
                onChange={(e) => setCurrentFee({ ...currentFee, amount: e.target.value })}
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>Date</Form.Label>
              <Form.Control
                type="date"
                value={currentFee.date}
                onChange={(e) => setCurrentFee({ ...currentFee, date: e.target.value })}
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>Remark</Form.Label>
              <Form.Control
                type="text"
                value={currentFee.remark}
                onChange={(e) => setCurrentFee({ ...currentFee, remark: e.target.value })}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseModal}>
            Cancel
          </Button>
          <Button variant="success" onClick={handleSaveFee}>
            {currentFee.id ? "Update" : "Save"}
          </Button>
        </Modal.Footer>
      </Modal>
    </Layout>
  );
};

export default FeeManagement;
